import { Bound, Currency, Price, Token } from '@cryptoalgebra/kim-sdk';
import { useMemo } from 'react';
import RangeSelectorPart from '../RangeSelectorPart';
import { Grid } from '@chakra-ui/react';
import { IDerivedMintInfo } from '@app/state/mintStore';
import {
  useMintActionHandlers,
  useRangeHopCallbacks
} from '@app/screens/liquidity/LiquidityWizard/state';

export interface RangeSelectorProps {
  priceLower: Price<Token, Token> | undefined;
  priceUpper: Price<Token, Token> | undefined;
  currencyA: Currency | null | undefined;
  currencyB: Currency | null | undefined;
  disabled: boolean;
  mintInfo: IDerivedMintInfo;
}

const RangeSelector = ({
  priceLower,
  priceUpper,
  currencyA,
  currencyB,
  disabled,
  mintInfo
}: RangeSelectorProps) => {
  const tokenA = (currencyA ?? undefined)?.wrapped;
  const tokenB = (currencyB ?? undefined)?.wrapped;

  const { [Bound.LOWER]: tickLower, [Bound.UPPER]: tickUpper } = useMemo(() => {
    return mintInfo.ticks;
  }, [mintInfo]);

  const {
    getDecrementLower,
    getIncrementLower,
    getDecrementUpper,
    getIncrementUpper
  } = useRangeHopCallbacks(
    currencyA ?? undefined,
    currencyB ?? undefined,
    mintInfo.tickSpacing,
    tickLower,
    tickUpper,
    mintInfo.pool
  );

  const { onLeftRangeInput, onRightRangeInput } = useMintActionHandlers(
    mintInfo.noLiquidity
  );

  const isSorted = useMemo(() => {
    return tokenA && tokenB && tokenA.sortsBefore(tokenB);
  }, [tokenA, tokenB]);

  const leftPrice = useMemo(() => {
    return isSorted ? priceLower : priceUpper?.invert();
  }, [isSorted, priceLower, priceUpper]);

  const rightPrice = useMemo(() => {
    return isSorted ? priceUpper : priceLower?.invert();
  }, [isSorted, priceUpper, priceLower]);

  return (
    <Grid gridTemplateColumns="1fr 1fr" gridGap={4}>
      <RangeSelectorPart
        value={
          mintInfo.ticksAtLimit[Bound.LOWER]
            ? '0'
            : leftPrice?.toSignificant(5) ?? ''
        }
        onUserInput={onLeftRangeInput}
        width="100%"
        decrement={isSorted ? getDecrementLower : getIncrementUpper}
        increment={isSorted ? getIncrementLower : getDecrementUpper}
        decrementDisabled={mintInfo.ticksAtLimit[Bound.LOWER]}
        incrementDisabled={mintInfo.ticksAtLimit[Bound.LOWER]}
        label={leftPrice ? `${currencyB?.symbol}` : '-'}
        initialPrice={mintInfo.price}
        disabled={disabled}
        title={'Min price'}
      />
      <RangeSelectorPart
        value={
          mintInfo.ticksAtLimit[Bound.UPPER]
            ? '∞'
            : rightPrice?.toSignificant(5) ?? ''
        }
        onUserInput={onRightRangeInput}
        decrement={isSorted ? getDecrementUpper : getIncrementLower}
        increment={isSorted ? getIncrementUpper : getDecrementLower}
        incrementDisabled={mintInfo.ticksAtLimit[Bound.UPPER]}
        decrementDisabled={mintInfo.ticksAtLimit[Bound.UPPER]}
        label={rightPrice ? `${currencyB?.symbol}` : '-'}
        initialPrice={mintInfo.price}
        disabled={disabled}
        title={`Max price`}
      />
    </Grid>
  );
};

export default RangeSelector;
